import axios from "axios";
import { useEffect, useMemo, useState } from "react";

import {
  COUNTRY_CHANGE_FOOTER_URL,
  MAGNOLIA_NEXT_API_URL,
  PRODUCT,
  SECONDARY_PRODUCT,
  INT_COUNTRY,
} from "@/constants";

import {
  CountryDetailsResponse,
  CountryFooterResponse,
  NormalizedCountryDetail,
} from "../country-change-modal/types/country-change-modal-types";
import {
  findDefaultCountry,
  normalizeCountryDetails,
} from "../country-change-modal/utils/country-change-footer-utils";

export function useCountryChangeFooter() {
  const [data, setData] = useState<CountryFooterResponse | null>(null);
  const [error, setError] = useState<Error | null>(null);
  const [loading, setLoading] = useState<boolean>(true);

  const url = `${MAGNOLIA_NEXT_API_URL}/${COUNTRY_CHANGE_FOOTER_URL}/${
    INT_COUNTRY ?? SECONDARY_PRODUCT ?? PRODUCT
  }`;

  useEffect(() => {
    const controller = new AbortController();
    const { signal } = controller;

    async function fetchData() {
      try {
        const response = await axios.get(url, { signal });
        if (response && response.data) {
          const footerData = response.data.data as CountryFooterResponse;
          setData(footerData);
        }
        setError(null);
      } catch (err) {
        if (axios.isCancel(err)) {
          return;
        }
        setError(err as Error);
        setData(null);
      } finally {
        setLoading(false);
      }
    }

    fetchData();

    return () => {
      controller.abort();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const normalizedCountries = useMemo(() => {
    return normalizeCountryDetails(
      data?.countryDetails as CountryDetailsResponse | undefined
    );
  }, [data?.countryDetails]);

  const defaultCountry = useMemo(() => {
    return findDefaultCountry(normalizedCountries) as NormalizedCountryDetail;
  }, [normalizedCountries]);

  return { data, error, loading, normalizedCountries, defaultCountry };
}
